(function () {
  "use strict";

  // Prevent duplicate listeners if the script is included twice.
  if (window.__shreyasiPreorderGuardLoaded) return;
  window.__shreyasiPreorderGuardLoaded = true;

  const MODAL_ID = "PreorderAckModal";
  const ACK_NAME = "properties[Preorder acknowledged]";

  let pending = null;
  let confirming = false;
  let replayForm = null;

  // Approvals belong to a specific form, variant and timeline.
  const approvals = new WeakMap();

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function isProductForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;

    try {
      const path = new URL(form.action, location.href).pathname;
      return /\/cart\/add(?:\.js)?\/?$/.test(path);
    } catch (_) {
      return false;
    }
  }

  function isPreorderButton(button) {
    const value = button?.getAttribute("data-shreyasi-preorder");
    return value === "true" || value === "1";
  }

  function findButton(form) {
    return form.querySelector(
      '[data-shreyasi-preorder="true"],' +
      '[data-shreyasi-preorder="1"]'
    );
  }

  function getMeta(button) {
    return {
      dispatch:
        button.getAttribute("data-shreyasi-dispatch-timeline") || "",
      addon:
        button.getAttribute("data-shreyasi-addon-timeline") || ""
    };
  }

  function signature(form, button) {
    const variant = form.querySelector('[name="id"]')?.value || "";
    const meta = getMeta(button);

    return JSON.stringify([
      variant,
      meta.dispatch,
      meta.addon
    ]);
  }

  function getProperty(form, name) {
    return Array.from(form.querySelectorAll("input")).find(
      input => input.name === name
    );
  }

  function setProperty(form, key, value) {
    const name = `properties[${key}]`;
    let input = getProperty(form, name);

    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      form.appendChild(input);
    }

    input.value = value;
    input.disabled = false;
  }

  function clearAcknowledgement(form) {
    approvals.delete(form);

    const input = getProperty(form, ACK_NAME);
    if (input) {
      input.value = "";
      input.disabled = true;
    }
  }

  function saveProperties(form, meta) {
    setProperty(form, "Preorder", "Yes");
    setProperty(form, "Dispatch timeline", meta.dispatch);

    const addon = getProperty(form, "properties[Add-on timeline]");
    if (meta.addon) {
      setProperty(form, "Add-on timeline", meta.addon);
    } else if (addon) {
      addon.value = "";
      addon.disabled = true;
    }

    // Set only after the customer clicks Confirm.
    setProperty(form, "Preorder acknowledged", "Yes");
  }

  function stop(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function closeModal() {
    const modal = getModal();
    if (modal) modal.hidden = true;
  }

  function cancel() {
    const job = pending;
    pending = null;
    closeModal();

    if (job) {
      clearAcknowledgement(job.form);
      job.button.focus();
    }
  }

  function requestConfirmation(event, form, button) {
    stop(event);

    if (confirming || pending) return;

    clearAcknowledgement(form);

    const modal = getModal();
    const text = modal?.querySelector("#PreorderAckText");

    // Do not silently continue if the popup markup is unavailable.
    if (!modal || !text) {
      console.error("[Shreyasi] Preorder popup markup is missing.");
      window.alert(
        "We couldn't display the preorder confirmation. " +
        "Please refresh this page and try again."
      );
      return;
    }

    const meta = getMeta(button);
    const parts = ["This item is available for pre-order."];

    if (meta.dispatch) {
      parts.push(`Estimated dispatch within ${meta.dispatch}.`);
    }

    if (meta.addon) {
      parts.push(
        `Optional services may add an additional ${meta.addon}.`
      );
    }

    parts.push(
      "By proceeding, you acknowledge that this is a pre-order item."
    );

    pending = {
      form,
      button,
      meta,
      signature: signature(form, button)
    };

    text.textContent = parts.join(" ");
    modal.hidden = false;
    modal.querySelector("[data-preorder-confirm]")?.focus();
  }

  function confirm() {
    if (!pending || confirming) return;

    const job = pending;

    // Recheck the item before recording acknowledgement.
    if (
      !job.form.isConnected ||
      !job.button.isConnected ||
      job.button.disabled ||
      signature(job.form, job.button) !== job.signature
    ) {
      cancel();
      window.alert(
        "The product selection changed. Please click Preorder again."
      );
      return;
    }

    confirming = true;
    pending = null;

    saveProperties(job.form, job.meta);
    approvals.set(job.form, job.signature);
    closeModal();

    // Replay the original button so Globo keeps its normal
    // validation, service charges and cart behaviour.
    replayForm = job.form;

    try {
      job.button.click();
    } catch (error) {
      clearAcknowledgement(job.form);
      console.error("[Shreyasi] Could not submit preorder.", error);
      window.alert("Please try adding the item again.");
    } finally {
      replayForm = null;
      confirming = false;
    }
  }

  // Register immediately, before app scripts.
  // Popup elements are located only when needed.
  window.addEventListener("click", function (event) {
    const target =
      event.target instanceof Element ? event.target : null;

    if (!target) return;

    const modal = target.closest("#" + MODAL_ID);

    if (modal && !modal.hidden) {
      if (target.closest("[data-preorder-confirm]")) {
        stop(event);
        confirm();
        return;
      }

      if (
        target.closest("[data-preorder-cancel], [data-preorder-close]")
      ) {
        stop(event);
        cancel();
        return;
      }
    }

    const button = target.closest(
      "[data-add-to-cart], button[name='add'], button[type='submit']"
    );

    if (!button || !isPreorderButton(button)) return;

    const form = button.form || button.closest("form");
    if (!isProductForm(form)) return;

    const approved =
      approvals.get(form) === signature(form, button);

    // Allow our replay and subsequent app-generated retries.
    // A new customer click always requests confirmation again.
    if (
      approved &&
      (replayForm === form || event.isTrusted === false)
    ) {
      return;
    }

    requestConfirmation(event, form, button);
  }, true);

  // Cover form submission, including keyboard submission.
  window.addEventListener("submit", function (event) {
    const form = event.target;
    if (!isProductForm(form)) return;

    const button = findButton(form);
    if (!button) return;

    if (approvals.get(form) === signature(form, button)) return;

    requestConfirmation(event, form, button);
  }, true);

  // Invalidate acknowledgement when the customer changes
  // a product selection, quantity or optional service.
  window.addEventListener("change", function (event) {
    const target = event.target;
    const form = target?.form || target?.closest?.("form");

    if (isProductForm(form) && findButton(form)) {
      clearAcknowledgement(form);
    }
  }, true);

  window.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && pending) {
      stop(event);
      cancel();
    }
  }, true);

  // Avoid retaining approval when returning through browser history.
  window.addEventListener("pageshow", function () {
    pending = null;
    closeModal();

    document.querySelectorAll("form").forEach(function (form) {
      if (isProductForm(form) && findButton(form)) {
        clearAcknowledgement(form);
      }
    });
  });

  console.log("[Shreyasi] Preorder confirmation guard loaded");
})();